import { useState, useEffect } from 'react';
import { Routes, Route, BrowserRouter as Router, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Discovery from './components/Discovery';
import Details from './components/details';
import Main from './Main';
import Layer from '@arcgis/core/layers/Layer';
import DerivationService from './services/DerivationService';
import esriConfig from '@arcgis/core/config';
import * as geometryJsonUtils from '@arcgis/core/geometry/support/jsonUtils';
import { LicenseInfo } from '@mui/x-license';
import Login from './Login';
import UserService from './services/UserService';
import FieldNames from './defaults/FieldNames.json';
import { useDispatch, useSelector } from 'react-redux';
import {
    setConfig,
    setNifcToken,
    setRootServiceUrl,
    setViewRootServiceUrl,
    setSummaryViewRootServiceUrl,
} from './redux/slices/configurationSlice';
import './App.css';
import AuthManager from './components/esri/auth/AuthManager';
import {
    getLegacyModifiedAttributes,
    getWFFTSModifiedAttributes,
    isFeaturePostFire,
} from './utils/FeatureHelper';
import UserAdminDialog from './components/popover/UserAdminDialog/UserAdminDialog';
import BudgetManagementDialog from './components/popover/BudgetManagementDialog/BudgetManagementDialog';
import CarryoverManagementDialog from './components/popover/CarryoverManagementDialog/CarryoverManagementDialog';
import DownloadDialog from './components/popover/DownloadDialog/DownloadDialog';
import logo from './images/IFPRS_Logo.png';
import { Dialog } from '@mui/material';
import ErrorService from './services/ErrorService';
import TargetManagementDialog from './components/popover/TargetManagementDialog/TargetManagementDialog';
import { ResetFeatureFundingLinesByFeature } from './utils/FundingHelper';
import { getSaveableDate } from './utils/DateHelper';
import ReportingDialog from './components/popover/ReportingDialog/ReportingDialog';
import BulkApprovalDialog from './components/popover/BulkApprovalDialog/BulkApprovalDialog';
import { validateCacheByProfile } from './utils/AdminHelper';
import packageInfo from '../package.json';
import { setDataset } from './redux/slices/filterSlice';
import AdminOverridesPanel from './components/adminOverrides/AdminOverridesPanel';
import { legacyRedirects, routes, Redirect } from './config/route-config';

import { setShowGlobalLoader, setUserInfo as setStoreUserInfo, setUserRoleInfo as setStoreUserRoleInfo } from '@store/slices/globalContextSlice';
import BulkEdit from '@components/BulkEdit/BulkEdit';
import { setDerivationErrorOpen, setDerivationError } from './redux/slices/errorSlice';

window.authManager = new AuthManager();

LicenseInfo.setLicenseKey(
    'a25856da2deafb4941fff0c39c371685Tz0xMjI2NjEsRT0xNzk2MzQyMzk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y'
); // TODO: Move to environment variable

export default function App() {
    /* Redux */
    const dispatch = useDispatch();

    /* Config */
    const basename = import.meta.env.BASE_URL; // TODO - EVAL: Why is this imported here? Move to config?

    /* Local State - Mostly drilled - TODO: Elevate to store */
    const [isTreatment, setIsTreatment] = useState(false);
    const [isCreatingPlannedFeature, setIsCreatingPlannedFeature] = useState(false);
    const [isCreatingActualFeature, setIsCreatingActualFeature] = useState(false);
    const [isEditingTreatment, setIsEditingTreatment] = useState(false);
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [isActuals, setIsActuals] = useState(false);
    const [isProject, setIsProject] = useState(false);
    const [project, setProject] = useState(null);
    const [listOpen, setListOpen] = useState(true);
    const [feature, setFeature] = useState(null);
    const [plannedFeature, setPlannedFeature] = useState(null);
    const [mode, setMode] = useState(FieldNames.modes.read);
    const [isNewFeature, setIsNewFeature] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [userCredential, setUserCredential] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [userRoleInfo, setUserRoleInfo] = useState([]);
    const [isUserFound, setIsUserFound] = useState(false);
    const [isConfigLoaded, setIsConfigLoaded] = useState(false);
    const [configFile, setConfigFile] = useState(null);
    const [isInReadWriteGroup, setIsInReadWriteGroup] = useState(false);
    const [isCheckingCredentials, setIsCheckingCredentials] = useState(true);
    const [headerheight, setHeaderHeight] = useState(125); // TODO: Correct name casing
    const [mapSnackbarMessage, setMapSnackbarMessage] = useState('');
    const [mapSnackbarSeverity, setMapSnackbarSeverity] = useState('success');
    const [mapSnackbarOpen, setMapSnackbarOpen] = useState(false);
    const [isError, setIsError] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState('');

    const showMap = useSelector((state) => state.displayState.showMap);

    // Keep Redux store in sync with local userInfo / userRoleInfo state
    useEffect(() => { dispatch(setStoreUserInfo(userInfo)); }, [userInfo, dispatch]);
    useEffect(() => { dispatch(setStoreUserRoleInfo(userRoleInfo)); }, [userRoleInfo, dispatch]);

    /* Returns common dialog props - NOTE: Some props are passed twice for compatibility with existing code */
    const getDialogProps = () => ({
        currentUserInfo: userInfo,
        userInfo,
        userRoleInfo,
        headerheight,
        headerHeight: headerheight,
    });

    /* Factory for creating <Details /> It passes all the common props, with ability to then pass overrides. */
    const DetailsFactory = (propOverrides = {}) => {
        const getDetailsDefaults = () => ({
            setIsTreatment,
            feature,
            setFeature,
            mode,
            setMode,
            setIsCreatingPlannedFeature,
            setIsCreatingActualFeature,
            isNewFeature,
            setIsNewFeature,
            isApproved,
            setIsApproved,
            isActuals,
            setIsActuals,
            selectedFeatures,
            setSelectedFeatures,
            plannedFeature,
            setPlannedFeature,
            userInfo,
            userRoleInfo,
            setIsCompleted,
            headerheight,
            rederiveFeatureInfo,
            isProject,
            setIsProject,
            project,
            setProject,
        });

        return <Details {...getDetailsDefaults()} {...propOverrides} />;
    };

    useEffect(() => {
        esriConfig.portalUrl = 'https://nifc.maps.arcgis.com'; //TODO: Unhardcode this

        window.api.getAppConfig().then((response) => {
            dispatch(setConfig(response));
            setIsConfigLoaded(true);
            setConfigFile(response);
            //DEV NOTE: Do NOT Put any Queries in here to external services -> It will interrupt the query to Auth and if it fails Auth thinks that the Login is failing
            //This will cause and infinite loop -> Crashes entire application
        });
    }, []);

    useEffect(() => {
        if (userInfo) {
            const isUserPostfire =
                userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes;
            const isUserFuels = userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes;
            const tempDataset =
                isUserPostfire && isUserFuels
                    ? 'FUELS'
                    : isUserPostfire && !isUserFuels
                      ? 'POSTFIRE'
                      : 'FUELS';
            dispatch(setDataset(tempDataset));
        }
    }, [userInfo]);

    useEffect(() => {
        if (!isConfigLoaded || !configFile) {
            return;
        }
        console.log('IFPRS Version ' + packageInfo.version + ' is starting up...');

        window.authManager = new AuthManager(
            configFile.appId[configFile.env],
            configFile.portalUrl,
            configFile.loginWithPopup
        );

        window.authManager.startup().then(
            () => {
                window.authManager.checkLogin().then(
                    (response) => {
                        const user = response.user;
                        const token = response.token;
                        dispatch(setNifcToken(token));
                        setUserCredential(user);
                        const sharingUrl =
                            'https://nifc.maps.arcgis.com/sharing/rest/content/items';
                        const proxyUrl = 'https://utility.arcgis.com';

                        //set the views url
                        Layer.fromPortalItem({
                            portalItem: {
                                id: configFile.targetTreatmentView[configFile.env].portalItem.id,
                            },
                        }).then(
                            (layer) => {
                                layer.load().then(() => {
                                    dispatch(setViewRootServiceUrl(layer.portalItem.url));
                                });
                            },
                            (error) => {
                                console.log(error);
                            }
                        );

                        //set the summary views url
                        Layer.fromPortalItem({
                            portalItem: {
                                id: configFile.summaryAttributesView[configFile.env].portalItem.id,
                            },
                        }).then(
                            (layer) => {
                                layer.load().then(() => {
                                    dispatch(setSummaryViewRootServiceUrl(layer.portalItem.url));
                                });
                            },
                            (error) => {
                                console.log(error);
                            }
                        );

                        // Creates a layer from a Portal layer item id
                        Layer.fromPortalItem({
                            portalItem: {
                                id: configFile.actionAttributes[configFile.env].portalItem.id,
                            },
                        }).then(
                            (layer) => {
                                layer.load().then(
                                    () => {
                                        dispatch(setRootServiceUrl(layer.portalItem.url));
                                        window.api
                                            .getUserGroups(user.username, token)
                                            .then((response) => {
                                                const inReadWriteGroup =
                                                    response.groups.filter((group) => {
                                                        return (
                                                            group.id ===
                                                            configFile.accessGroupId[configFile.env]
                                                        );
                                                    }).length === 1;
                                                setIsInReadWriteGroup(inReadWriteGroup);
                                                const currentlyNonProduction =
                                                    configFile.env !== 'prod';
                                                let inNoGroups = !inReadWriteGroup;
                                                if (currentlyNonProduction && inNoGroups) {
                                                    const inProductionReadWriteGroup =
                                                        response.groups.filter((group) => {
                                                            return (
                                                                group.id ===
                                                                configFile.accessGroupId['prod']
                                                            );
                                                        }).length === 1;
                                                    if (inProductionReadWriteGroup) {
                                                        setIsInReadWriteGroup(
                                                            inProductionReadWriteGroup
                                                        );
                                                        inNoGroups = false;
                                                    }
                                                }

                                                if (inNoGroups) {
                                                    setUserInfo(null);
                                                    setUserRoleInfo([]);
                                                    setIsUserFound(false);
                                                } else {
                                                    UserService.getUserByNifcUsername(
                                                        layer.portalItem.url,
                                                        user.username,
                                                        configFile
                                                    )
                                                        .then((response) => {
                                                            if (response && response.length > 0) {
                                                                setUserInfo(response[0]);
                                                                validateCacheByProfile(response[0]);
                                                                if (response[0]) {
                                                                    setIsUserFound(true);
                                                                    UserService.getUserRoles(
                                                                        layer.portalItem.url,
                                                                        response[0][
                                                                            FieldNames.userFields.id
                                                                        ],
                                                                        configFile
                                                                    )
                                                                        .then((userRoles) => {
                                                                            setIsCheckingCredentials(
                                                                                false
                                                                            );
                                                                            if (
                                                                                userRoles &&
                                                                                userRoles.length > 0
                                                                            ) {
                                                                                setUserRoleInfo(
                                                                                    userRoles
                                                                                );
                                                                            } else {
                                                                                setUserRoleInfo([]);
                                                                            }
                                                                        })
                                                                        .catch((error) => {
                                                                            ErrorService.addErrorLogEntry(
                                                                                'SYSTEM',
                                                                                error
                                                                            );
                                                                        });
                                                                } else {
                                                                    setIsUserFound(false);
                                                                }

                                                                let user = response[0];
                                                                user[
                                                                    FieldNames.userFields.lastLogin
                                                                ] = getSaveableDate(new Date());
                                                                UserService.updateUser(
                                                                    layer.portalItem.url,
                                                                    token,
                                                                    user,
                                                                    configFile
                                                                )
                                                                    .then((updatedResp) => {
                                                                        if (
                                                                            updatedResp &&
                                                                            updatedResp.updateResults &&
                                                                            updatedResp
                                                                                .updateResults
                                                                                .length > 0
                                                                        ) {
                                                                            // TODO - EVAL: No action at this time?
                                                                        }
                                                                    })
                                                                    .catch((error) => {
                                                                        ErrorService.addErrorLogEntry(
                                                                            'SYSTEM',
                                                                            error
                                                                        );
                                                                    });
                                                            } else {
                                                                setIsUserFound(false); //TODO: We need to throw something in the UI here...
                                                            }
                                                        })
                                                        .catch((error) => {
                                                            ErrorService.addErrorLogEntry(
                                                                'SYSTEM',
                                                                error
                                                            );
                                                        });
                                                }
                                            });
                                    },
                                    (error) => {
                                        setIsUserFound(false);
                                        setIsError(true);
                                        if (error) {
                                            setLoginErrorMessage(error.message);
                                            ErrorService.addErrorLogEntry('SYSTEM', error);
                                        }
                                    }
                                );
                            },
                            (error) => {
                                setIsUserFound(false);
                                setIsError(true);
                                if (error) {
                                    setLoginErrorMessage(error.message);
                                    ErrorService.addErrorLogEntry('SYSTEM', error);
                                }
                            }
                        );
                    },
                    (error) => {
                        setIsUserFound(false);
                        if (error && error !== 'User is not logged in') {
                            setIsError(true);
                            if (error) {
                                setLoginErrorMessage(error.message);
                                ErrorService.addErrorLogEntry('SYSTEM', error);
                            }
                        }
                    }
                );
            },
            (error) => {
                setIsUserFound(false);
                setIsError(true);
                if (error) {
                    setLoginErrorMessage(error.message);
                    ErrorService.addErrorLogEntry('SYSTEM', error);
                }
            }
        );
    }, [isConfigLoaded, configFile]);

    const logout = () => {
        setUserCredential(null);
        setUserInfo(null);
        window.authManager.logout();
    };

    // TODO: Extract to service layer. Depends on:
    // - local state,
    // - redux dispatch and eventually may depend on selectors
    // - various helpers and services that are used within the function
    // Can't be easily moved without refactor - leaving as a 'drilled' prop for now
    const rederiveFeatureInfo = async (
        feature,
        isTreatment,
        isFeatureActuals,
        isPoint,
        wfftsAttributes,
        legacyAttributes,
        resetAcreage = true
    ) => {
        dispatch(setShowGlobalLoader(true));
        let plannedFields = FieldNames.featureFields;
        if (
            feature?.attributes[FieldNames.featureFields.isPoint] == FieldNames.yesNo.yes &&
            (feature?.attributes[FieldNames.featureFields.fundingUnitType] ==
                FieldNames.unitTypes.national ||
                feature?.attributes[FieldNames.featureFields.fundingUnitType] ==
                    FieldNames.unitTypes.regional) &&
            feature?.attributes[FieldNames.featureFields.category] ==
                FieldNames.categoriesForChecking.programManagement
        ) {
            return;
        }
        const isPostFire = isFeaturePostFire(feature?.attributes);
        if (isFeatureActuals || (!isFeatureActuals && wfftsAttributes)) {
            let actualFields = FieldNames.featureFields;
            if (!isTreatment) {
                actualFields = FieldNames.featureFields;
                plannedFields = FieldNames.featureFields;
            }

            const perimeter = geometryJsonUtils.fromJSON(feature.geometry);
            const isManual =
                feature &&
                feature.attributes &&
                feature.attributes[plannedFields.isVegetationManual] === FieldNames.yesNo.yes
                    ? true
                    : false;
            DerivationService.deriveAttributesForGeometry(
                perimeter,
                false,
                isTreatment,
                feature?.attributes[FieldNames.featureFields.joinFeature],
                isFeatureActuals,
                configFile,
                userRoleInfo,
                isManual,
                isPostFire,
                isPoint
            )
                .then((attributes) => {
                    let tempAttr;
                    if (attributes && attributes.attributes) {
                        tempAttr = { ...feature.attributes, ...attributes.attributes };
                    } else {
                        tempAttr = { ...feature.attributes };
                    }
                    const tempPeri = feature.geometry;
                    let tempFeatureAttr;
                    if (wfftsAttributes) {
                        tempFeatureAttr = getWFFTSModifiedAttributes(
                            tempAttr,
                            isTreatment,
                            isFeatureActuals,
                            wfftsAttributes
                        );
                    } else if (legacyAttributes) {
                        tempFeatureAttr = getLegacyModifiedAttributes(
                            tempAttr,
                            legacyAttributes,
                            actualFields
                        );
                    } else {
                        tempFeatureAttr = getLegacyModifiedAttributes(tempAttr, null, actualFields);
                    }

                    let attrObjectID = null;
                    if (tempAttr) {
                        attrObjectID = tempAttr[FieldNames.featureFields.objectID];
                    }
                    if (attrObjectID && resetAcreage) {
                        ResetFeatureFundingLinesByFeature(tempFeatureAttr, userInfo).then(
                            (resp) => {
                                tempFeatureAttr[FieldNames.featureFields.isFundingAcresManual] =
                                    FieldNames.yesNo.no;
                                let tempFeature = {
                                    attributes: tempFeatureAttr,
                                    geometry: tempPeri,
                                };
                                setFeature(tempFeature);
                                dispatch(setShowGlobalLoader(false));
                                return tempFeature;
                            }
                        );
                    } else {
                        let tempFeature = {
                            attributes: tempFeatureAttr,
                            geometry: tempPeri,
                        };
                        setFeature(tempFeature);
                        dispatch(setShowGlobalLoader(false));
                        return tempFeature;
                    }
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        userRoleInfo[FieldNames.userFields.nifcUsername],
                        error
                    );
                    dispatch(setShowGlobalLoader(false));
                })
                .finally(() => {});
        } else {
            const perimeter = geometryJsonUtils.fromJSON(feature.geometry);
            const isManual =
                feature &&
                feature.attributes &&
                feature.attributes[plannedFields.isVegetationManual] === FieldNames.yesNo.yes
                    ? true
                    : false;
            DerivationService.deriveAttributesForGeometry(
                perimeter,
                false,
                isTreatment,
                feature?.attributes[FieldNames.featureFields.joinFeature],
                isFeatureActuals,
                configFile,
                userRoleInfo,
                isManual,
                isPostFire,
                isPoint
            )
                .then((attributes) => {
                    let tempFeature;
                    let tempAttr;
                    let tempPeri = feature.geometry;
                    if (attributes?.attributes) {
                        tempAttr = { ...feature.attributes, ...attributes.attributes };
                        if (attributes.error) {
                            dispatch(setDerivationError(attributes.error));
                            dispatch(setDerivationErrorOpen(true));
                        }
                    } else {
                        tempAttr = { ...feature.attributes };
                    }

                    let attrObjectID = null;
                    if (tempAttr) {
                        attrObjectID = tempAttr[FieldNames.featureFields.objectID];
                    }
                    if (attrObjectID && resetAcreage) {
                        ResetFeatureFundingLinesByFeature(tempAttr, userInfo).then((resp) => {
                            tempAttr[FieldNames.featureFields.isFundingAcresManual] =
                                FieldNames.yesNo.no;
                            tempFeature = {
                                attributes: tempAttr,
                                geometry: tempPeri,
                            };
                            setFeature(tempFeature);
                            dispatch(setShowGlobalLoader(false));
                            return tempFeature;
                        });
                    } else {
                        tempFeature = {
                            attributes: tempAttr,
                            geometry: tempPeri,
                        };
                        setFeature(tempFeature);
                        dispatch(setShowGlobalLoader(false));
                        return tempFeature;
                    }
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        userRoleInfo[FieldNames.userFields.nifcUsername],
                        error
                    );
                    dispatch(setShowGlobalLoader(false));
                })
                .finally(() => {});
        }
    };

    return isError && loginErrorMessage !== 'User is not logged in' ? (
        <Dialog open={true}>
            <div className='login-error__message-container'>
                <img src={logo} alt='IFPRS' />
                <p>Error: {loginErrorMessage}</p>
                <p>IFPRS has encountered an error and cannot proceed.</p>
                <p>You may try reloading your browser.</p>
            </div>
        </Dialog>
    ) : userInfo && isUserFound && isInReadWriteGroup && userRoleInfo && userRoleInfo.length > 0 ? (
        <div className='app-content' style={{ display: showMap ? 'flex' : 'block' }}>
            <Router basename={basename}>
                <Header
                    isTreatment={isTreatment}
                    isCreatingPlannedFeature={isCreatingPlannedFeature}
                    setIsCreatingPlannedFeature={setIsCreatingPlannedFeature}
                    isCreatingActualFeature={isCreatingActualFeature}
                    setIsCreatingActualFeature={setIsCreatingActualFeature}
                    isEditingTreatment={isEditingTreatment}
                    setIsEditingTreatment={setIsEditingTreatment}
                    feature={feature}
                    setFeature={setFeature}
                    mode={mode}
                    rederiveFeatureInfo={rederiveFeatureInfo}
                    isNewFeature={isNewFeature}
                    isApproved={isApproved}
                    isActuals={isActuals}
                    setIsTreatment={setIsTreatment}
                    plannedFeature={plannedFeature}
                    userInfo={userInfo}
                    setUserInfo={setUserInfo}
                    userRoleInfo={userRoleInfo}
                    setUserRoleInfo={setUserRoleInfo}
                    logout={logout}
                    isCompleted={isCompleted}
                    headerHeight={headerheight}
                    setHeaderHeight={setHeaderHeight}
                    mapSnackbarMessage={mapSnackbarMessage}
                    setMapSnackbarMessage={setMapSnackbarMessage}
                    mapSnackbarOpen={mapSnackbarOpen}
                    setMapSnackbarOpen={setMapSnackbarOpen}
                    mapSnackbarSeverity={mapSnackbarSeverity}
                    setMapSnackbarSeverity={setMapSnackbarSeverity}
                    isProject={isProject}
                />

                <Routes>
                    {legacyRedirects.flatMap(({ from, to }) =>
                        from.map((path) => (
                            <Route key={path} path={path} element={<Redirect to={to} />} />
                        ))
                    )}

                    {routes.newProject.map((path) => (
                        <Route
                            key={path}
                            path={path}
                            element={DetailsFactory({ isTreatment: true })}
                        />
                    ))}

                    {routes.existingProject.map((path, i) => (
                        <Route
                            key={i}
                            path={path}
                            element={DetailsFactory({
                                isTreatment: true,
                            })}
                        />
                    ))}

                    {routes.existingTreatment.map((path, i) => (
                        <Route
                            key={i}
                            path={path}
                            element={DetailsFactory({ isTreatment: true })}
                        />
                    ))}

                    {routes.newTreatment.map((path, i) => (
                        <Route
                            key={i}
                            path={path}
                            element={DetailsFactory({ isTreatment: true })}
                        />
                    ))}

                    {routes.existingActivities.map((path, i) => (
                        <Route
                            key={i}
                            path={path}
                            element={DetailsFactory({ isTreatment: false })}
                        />
                    ))}

                    {routes.newActivities.map((path, i) => (
                        <Route
                            key={i}
                            path={path}
                            element={DetailsFactory({ isTreatment: false })}
                        />
                    ))}

                    <Route
                        path='/home'
                        element={
                            <Discovery
                                setIsCreatingPlannedFeature={setIsCreatingPlannedFeature}
                                setIsCreatingActualFeature={setIsCreatingActualFeature}
                                setIsTreatment={setIsTreatment}
                                isTreatment={isTreatment}
                                listOpen={listOpen}
                                setListOpen={setListOpen}
                                setIsEditingTreatment={setIsEditingTreatment}
                                selectedFeatures={selectedFeatures}
                                setSelectedFeatures={setSelectedFeatures}
                                setMode={setMode}
                                setPlannedFeature={setPlannedFeature}
                                isCreatingActualFeature={isCreatingActualFeature}
                                isCreatingPlannedFeature={isCreatingPlannedFeature}
                                userInfo={userInfo}
                                userRoleInfo={userRoleInfo}
                                headerheight={headerheight}
                            />
                        }
                    />

                    <Route
                        path='/admin/users'
                        element={<UserAdminDialog {...getDialogProps()} />}
                    />
                    <Route
                        path='/admin/overrides'
                        element={<AdminOverridesPanel {...getDialogProps()} />}
                    />
                    <Route
                        path='/workflow/bulkapproval'
                        element={<BulkApprovalDialog {...getDialogProps()} />}
                    />
                    <Route
                        path='/workflow/budget'
                        element={<BudgetManagementDialog {...getDialogProps()} />}
                    />
                    <Route
                        path='/workflow/carryover'
                        element={<CarryoverManagementDialog {...getDialogProps()} />}
                    />
                    <Route
                        path='/workflow/downloads'
                        element={<DownloadDialog {...getDialogProps()} />}
                    />
                    <Route
                        path='/workflow/targets'
                        element={<TargetManagementDialog {...getDialogProps()} />}
                    />
                    <Route
                        path='/workflow/reporting'
                        element={<ReportingDialog {...getDialogProps()} />}
                    />
                    <Route
                        path='/workflow/bulkedit'
                        element={
                            userRoleInfo?.length > 0 &&
                            (userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.department ||
                                userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.agency)
                                ? <BulkEdit {...getDialogProps()} />
                                : <Navigate to='/home' replace />
                        }
                    />
                </Routes>

                {/* -- This Component contains the actual map -- */}
                <Main
                    listOpen={listOpen}
                    isConfigLoaded={isConfigLoaded}
                    userInfo={userInfo}
                    userRoleInfo={userRoleInfo}
                    mapSnackbarMessage={mapSnackbarMessage}
                    setMapSnackbarMessage={setMapSnackbarMessage}
                    mapSnackbarOpen={mapSnackbarOpen}
                    setMapSnackbarOpen={setMapSnackbarOpen}
                    mapSnackbarSeverity={mapSnackbarSeverity}
                    setMapSnackbarSeverity={setMapSnackbarSeverity}
                    headerheight={headerheight}
                    setIsActuals={setIsActuals}
                />
            </Router>
        </div>
    ) : (
        <Login
            userCredential={userCredential}
            isUserFound={isUserFound}
            logout={logout}
            isInReadWriteGroup={isInReadWriteGroup}
            userRoleInfo={userRoleInfo}
            isCheckingCredentials={isCheckingCredentials}
        />
    );
}
